import React from 'react';
import { V } from '../../../utils/theme';
import { DASH, numText, pctText } from './helpers';

// ─── Presentational bits shared by the Org Analytics screen and the Business
// Value white-label tab. Every style is built inside a render function: V is a
// mutable singleton that applyBrand()/setTheme() overwrite, so a style captured
// at module scope would freeze the old brand.
//
// iOS installed-PWA rules apply here: explicit padding properties (never the
// `padding` shorthand, which Safari drops when it cannot parse one side) and a
// 44px minimum for anything tappable.

/** Semantic colours — resolved per render so a re-brand repaints. */
export const tone = (t) => (t === 'error' ? V.danger : t === 'warn' ? V.warn : t === 'ok' ? '#22c55e' : V.accent);
export const seriesColors = () => [V.accent, V.accent2, V.purple, V.warn, '#22c55e', '#06b6d4'];
export const STATUS_TONE = { active: '#22c55e', suspended: '#f59e0b', closed: '#ef4444' };

/** Dense KPI tile. `value` is already formatted (numText / pctText / ratioText). */
export const Tile = ({ label, value, sub, color, icon, testId, big }) => (
  <div data-testid={testId}
    style={{
      paddingTop: big ? 14 : 10, paddingBottom: big ? 14 : 10, paddingLeft: 12, paddingRight: 12,
      borderRadius: 12, background: V.card, border: `1px solid ${color || V.cardBorder}22`, minWidth: 0,
    }}>
    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
      {icon && <span style={{ fontSize: 12 }}>{icon}</span>}
      <div style={{ fontSize: 9, fontWeight: 700, color: V.text3, textTransform: "uppercase", letterSpacing: ".06em", lineHeight: 1.2 }}>{label}</div>
    </div>
    <div style={{ fontSize: big ? 22 : 16, fontWeight: 900, color: color || V.text, fontFamily: V.mono, lineHeight: 1.1, wordBreak: "break-word" }}>{value}</div>
    {sub !== undefined && sub !== null && sub !== '' && <div style={{ fontSize: 9, color: V.text3, marginTop: 3, lineHeight: 1.4 }}>{sub}</div>}
  </div>
);

/** Auto-fitting tile grid — one column on a phone, more when there is room. */
export const TileGrid = ({ children, min = 132, style = {} }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 6, ...style }}>{children}</div>
);

/**
 * One CSS bar row, in two flavours decided by whether `pct` was passed at all:
 *
 *  - pass `pct` (a ratio row): the number shown is the percentage. `pct === null`
 *    means the denominator was 0, so it renders an em dash over an empty track —
 *    never "0%", which would claim a measurement nobody took.
 *  - omit `pct` and pass `max` (a count row): the number shown is the count and
 *    the width is its share of the largest row.
 */
export const BarRow = ({ label, value, pct, max, color, testId, hint }) => {
  const c = color || V.accent;
  const isPctRow = pct !== undefined;
  const pctNum = typeof pct === 'number' && Number.isFinite(pct) ? pct : null;
  const missing = isPctRow && pctNum === null;
  const width = isPctRow
    ? (pctNum === null ? 0 : Math.max(0, Math.min(100, pctNum)))
    : (max > 0 ? Math.max(0, Math.min(100, ((Number(value) || 0) / max) * 100)) : 0);
  const shown = isPctRow ? pctText(pct) : numText(value);
  const suffix = isPctRow && value !== undefined && value !== null ? ` · ${numText(value)}` : '';
  return (
    <div data-testid={testId} style={{ paddingTop: 4, paddingBottom: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: V.text2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: shown === DASH ? V.text3 : c, fontFamily: V.mono, flexShrink: 0 }}>
          {shown}{suffix ? <span style={{ color: V.text3, fontWeight: 500 }}>{suffix}</span> : null}
        </span>
      </div>
      <div style={{
        height: 8, borderRadius: 8, overflow: "hidden",
        background: V.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)",
        border: missing ? `1px dashed ${V.text3}55` : "none", boxSizing: "border-box",
      }}>
        <div style={{ width: `${width}%`, height: "100%", borderRadius: 8, background: `linear-gradient(90deg,${c}cc,${c})`, transition: "width .4s ease" }} />
      </div>
      {hint && <div style={{ fontSize: 9, color: V.text3, marginTop: 3 }}>{hint}</div>}
    </div>
  );
};

/** Custom chart legend (recharts' own does not follow V on a re-brand). */
export const ChartLegend = ({ items }) => (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
    {items.map((it) => (
      <div key={it.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: it.color, border: it.outline ? `1px dashed ${it.color}` : "none", flexShrink: 0 }} />
        <span style={{ fontSize: 9, color: V.text3 }}>{it.label}</span>
      </div>
    ))}
  </div>
);

export const StatusPill = ({ status }) => (
  <span style={{
    fontSize: 9, fontWeight: 700, color: STATUS_TONE[status] || V.text3, paddingTop: 2, paddingBottom: 2,
    paddingLeft: 7, paddingRight: 7, borderRadius: 8, background: `${STATUS_TONE[status] || '#888'}18`,
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  }}>{status || 'unknown'}</span>
);

/** Table cell — explicit paddings, wrapped by the caller in an overflow-x container. */
export const td = {
  paddingTop: 7, paddingBottom: 7, paddingLeft: 8, paddingRight: 8,
  borderBottom: `1px solid rgba(128,128,128,0.12)`, whiteSpace: "nowrap", fontSize: 11,
};
export const th = { ...td, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", textAlign: "left" };

/** Uppercase micro-heading inside a Card. */
export const Label = ({ children, color, style = {} }) => (
  <div style={{ fontSize: 9, fontWeight: 700, color: color || V.text3, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 6, ...style }}>{children}</div>
);

/** Small square-ish toolbar button — 44px tall so it is tappable on iOS. */
export const ToolBtn = ({ children, onClick, disabled, testId, ariaLabel }) => (
  <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} data-testid={testId} aria-label={ariaLabel}
    style={{
      minHeight: 44, minWidth: 44, paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12,
      borderRadius: 10, background: V.hoverBg, border: `1px solid ${V.cardBorder}`, color: V.text2,
      fontSize: 11, fontWeight: 700, fontFamily: V.font, cursor: disabled ? "default" : "pointer",
      opacity: disabled ? .5 : 1, WebkitTapHighlightColor: "transparent", whiteSpace: "nowrap",
    }}>{children}</button>
);
